import React from 'react';
import { Box, Flex, Text, Icon, useColorModeValue } from '@chakra-ui/react';
import { NavLink, useLocation } from 'react-router-dom';
import { FiHome, FiGrid, FiUsers, FiCheckCircle, FiActivity, FiSettings, FiMessageCircle, FiBell } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { getUnionFeatures } from '../utils/institutionFeatures';
import useChatEnabled from '../mobile/hooks/useChatEnabled';

import { motion, AnimatePresence } from 'framer-motion';

const NavItem = ({ icon, label, to, variant }) => {
    const location = useLocation();
    const isActive = location.pathname === to;
    const isFloating = variant === 'floating';

    // Default Styles (Staff/Admin)
    if (!isFloating) {
        return (
            <Flex
                as={NavLink}
                to={to}
                direction="column"
                align="center"
                justify="center"
                flex="1"
                py={2}
                // 這個變體的容器自己就是 bg:"canvas"（見下方 containerStyles），
                // 所以文字直接壓在畫布上，必須用 onCanvas 階而非 gray/brand
                //（gray.400 在畫布上只有 2.03、brand.600 只有 2.53，都不達標）。
                color={isActive ? "onCanvas.500" : "onCanvas.300"}
                _activeLink={{ color: "onCanvas.500" }}
                style={{ textDecoration: 'none' }}
                transition="all 0.2s"
            >
                <Icon as={icon} boxSize={6} mb={1} />
                <Text fontSize="xs" fontWeight={isActive ? "bold" : "normal"}>
                    {label}
                </Text>
            </Flex>
        );
    }

    // Floating Variant Styles (Parent - Shifting Navigation)
    return (
        <Box
            as={NavLink}
            to={to}
            position="relative"
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{ textDecoration: 'none' }}
        >
            <motion.div
                layout
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    borderRadius: '9999px',
                    position: 'relative',
                    zIndex: 1,
                    gap: isActive ? '8px' : '0px',
                    color: isActive ? 'var(--chakra-colors-blue-600)' : 'var(--chakra-colors-gray-400)',
                }}
            >
                {/* Active Pill Background */}
                {isActive && (
                    <motion.div
                        layoutId="active-pill"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            backgroundColor: 'var(--chakra-colors-blue-50)',
                            borderRadius: '9999px',
                            zIndex: -1,
                        }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                )}

                {/* Icon */}
                <Icon as={icon} boxSize={7} zIndex={2} />

                {/* Label (Only when active) */}
                <AnimatePresence>
                    {isActive && (
                        <motion.span
                            initial={{ opacity: 0, x: -10, width: 0 }}
                            animate={{ opacity: 1, x: 0, width: "auto" }}
                            exit={{ opacity: 0, x: -10, width: 0 }}
                            transition={{ duration: 0.2 }}
                            style={{
                                whiteSpace: 'nowrap',
                                fontWeight: 'bold',
                                fontSize: '14px', // sm
                                overflow: 'hidden',
                                zIndex: 2,
                            }}
                        >
                            {label}
                        </motion.span>
                    )}
                </AnimatePresence>
            </motion.div>
        </Box>
    );
};

const MobileBottomNav = ({ display, variant = 'default' }) => {
    const { user } = useAuth();
    const role = user?.role;
    // [B3] Hooks 規則：不可放進下面 if(role==='parent') 分支，一律頂層呼叫。
    // chat_enabled 預設/查詢中都是 false，非家長角色呼叫這行只是白白暖機快取，無副作用。
    const chatFlagOn = useChatEnabled();

    // ... (Menus definition) ...
    // Limit to 3-5 items for mobile space
    let menus = [];

    if (role === 'parent') {
        // [A2] 名下孩子機構 features 的聯集：單幼稚園孩子家庭（announcements/comm_book 關閉）
        // 只會看到「首頁」「設定」。天籟家庭（features 全開）四個項目都在，逐字不變。
        const features = getUnionFeatures(user?.children);
        // [B3] 休眠出貨：全域旗標 off（prod 現況）或沒有任何孩子的機構開 chat 時，
        // 「聯絡簿」入口目的地不變（零可見變化）；兩者都通過才指向新的對話列表。
        const chatReady = chatFlagOn && features.chat;
        menus = [
            { label: '首頁', icon: FiHome, to: '/parent/dashboard' },
            ...(features.announcements ? [{ label: '公告', icon: FiBell, to: '/parent/announcements' }] : []),
            ...(features.comm_book ? [{ label: '聯絡簿', icon: FiMessageCircle, to: chatReady ? '/parent/chat' : '/parent/communication-book' }] : []),
            { label: '設定', icon: FiSettings, to: '/parent/settings' },
        ];
    } else if (['teacher'].includes(role)) {
        menus = [
            { label: '儀表板', icon: FiHome, to: '/dashboard' },
            { label: '我的班級', icon: FiGrid, to: '/my-class' },
            { label: '班級點名', icon: FiCheckCircle, to: '/roll-call' },
            { label: '設定', icon: FiSettings, to: '/settings' },
        ];
    } else {
        // Admin / Receptionist / Super Admin
        menus = [
            { label: '儀表板', icon: FiHome, to: '/dashboard' },
            { label: '狀況', icon: FiActivity, to: '/institution-status' },
            { label: '點名', icon: FiCheckCircle, to: '/daily-attendance' },
            { label: '控台', icon: FiActivity, to: '/pickup-monitor' },
            { label: '設定', icon: FiSettings, to: '/settings' },
        ];
    }

    // Default Container Styles (Flat)
    let containerStyles = {
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        // 底部導航用畫布色、不畫上緣線也不加陰影：新配色下 App 外框（含 iOS 安全區）
        // 是黃畫布，導航列若維持白底＋上緣線＋陰影，會在畫面底部形成一條突兀的橫帶。
        // 與老師端 PWA 的處理一致（ExpandableBottomNav 同樣改為 canvas 且移除分隔線）。
        bg: "canvas",
        zIndex: 1000,
        pb: "safe-area-inset-bottom",
    };

    // Floating Variant Styles (Glassmorphism & Rounded)
    if (variant === 'floating') {
        containerStyles = {
            ...containerStyles,
            position: "fixed",
            left: "20px",
            right: "20px",
            borderTop: "none",
            borderRadius: "full", // Round pill shape
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)", // Deeper shadow
            bg: "rgba(255, 255, 255, 0.85)", // Semi-transparent
            backdropFilter: "blur(12px)", // Glass effect
            pb: 0, // No safe area needed for floating element usually (or handled by bottom margin)
            // But we might need safe area if 20px isn't enough on some devices. 
            // Better to stick to bottom: calc(20px + env(safe-area-inset-bottom))
            bottom: "calc(20px + env(safe-area-inset-bottom, 20px))",
            // 打字時整條讓位：外殼縮到鍵盤上緣後，這顆浮動膠囊會正好壓在輸入列上。
            // 用 <html data-keyboard>（由 useKeyboardViewport 設定）以純 CSS 判斷，不觸發 re-render。
            'data-hide-on-keyboard': true,
        };
    } else {
        containerStyles = {
            ...containerStyles,
            pb: "6px",
        };
    }

    return (
        <Box
            {...containerStyles}
            display={display || { base: 'block', md: 'none' }}
        >
            <Flex justify="space-around" align="center" h={variant === 'floating' ? "64px" : "56px"} px={variant === 'floating' ? 2 : 0}>
                {menus.map((menu) => (
                    <NavItem key={menu.to} {...menu} variant={variant} />
                ))}
            </Flex>
        </Box>
    );
};

export default MobileBottomNav;
